import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Loader2, Mail, Lock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import Logo from '@/components/Logo';

export default function Login() {
  const { login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  // Default post-login destination depends on the user's role:
  //  - admins keep the full Advanced Dashboard at "/"
  //  - everyone else lands on the simplified one-glance view
  // If the user was redirected here from a protected page, honour that.
  const fallbackFor = (u) => (u?.role === 'admin' ? '/' : '/simple-dashboard');
  const redirectTo = location.state?.from;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!email || !password) { setErr(t('auth.emailRequired')); return; }
    setLoading(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success(t('auth.welcome', { name: u.name || u.email }));
      navigate(redirectTo || fallbackFor(u), { replace: true });
    } catch (e) {
      const msg = e?.response?.data?.detail || t('auth.invalidCreds');
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 py-10" data-testid="login-page">
      <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-10 items-center">
        <div className="hidden lg:flex flex-col gap-6" data-testid="login-hero">
          <Logo size={52} variant="full" tagline />
          <div>
            <h1 className="text-4xl xl:text-5xl font-heading font-bold tracking-tight leading-tight">
              Audit‑Ready Carbon Compliance Infrastructure
            </h1>
            <p className="mt-4 text-base text-muted-foreground leading-relaxed">
              Veridex One helps industrial suppliers reduce CBAM risk, streamline supplier
              data collection, and deliver audit‑ready carbon intelligence — so you can
              comply with confidence.
            </p>
          </div>
          <div>
            <Link to="/pilot-request" data-testid="hero-pilot-cta">
              <button className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors">
                Book Your CBAM Readiness Pilot
                <span aria-hidden="true">→</span>
              </button>
            </Link>
            <p className="mt-3 text-xs text-muted-foreground">
              60–90 day engagement · Priced from €3,000 · Credit applied to annual plan
            </p>
          </div>
          <ul className="grid grid-cols-1 gap-2 text-sm text-muted-foreground mt-2">
            {[
              'CBAM quarterly reports + ICS2 ENS customs XML',
              'Human‑review workflow with approval audit trail',
              'Transparent methodology, Open CEDA factors, IPCC 2024 GWP',
            ].map((b) => (
              <li key={b} className="flex items-start gap-2">
                <span className="text-primary mt-0.5">✓</span> {b}
              </li>
            ))}
          </ul>
        </div>

        <div className="w-full max-w-md mx-auto lg:mx-0">
          <div className="flex items-center justify-center lg:justify-start gap-2 mb-8 lg:hidden">
            <Logo size={44} variant="full" tagline />
          </div>

          <Card className="p-8 shadow-lg border-border" data-testid="login-card">
          <div className="mb-6">
            <h1 className="text-2xl font-bold tracking-tight">{t('auth.signInTitle')}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t('auth.signInSubtitle')}</p>
          </div>

          {err && (
            <div className="flex items-center gap-2 p-3 mb-4 rounded-md bg-destructive/10 border border-destructive/30 text-destructive text-sm" data-testid="login-error">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{err}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="email" data-testid="login-email-input" type="email" value={email}
                       onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
                       className="pl-9 rtl:pl-3 rtl:pr-9" autoComplete="email" required />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t('auth.password')}</Label>
                <Link to="/forgot-password"
                      className="text-xs text-muted-foreground hover:text-primary" data-testid="forgot-password-link">
                  {t('auth.forgotPassword')}
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="password" data-testid="login-password-input" type="password" value={password}
                       onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                       className="pl-9 rtl:pl-3 rtl:pr-9" autoComplete="current-password" required />
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full" data-testid="login-submit-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('auth.signingIn')}</> : t('auth.signIn')}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-border text-center text-sm">
            <span className="text-muted-foreground">{t('auth.noAccount')} </span>
            <Link to="/register" className="text-primary hover:underline font-medium" data-testid="go-to-register-link">
              {t('auth.createAccount')}
            </Link>
          </div>
        </Card>

        <nav className="mt-6 flex items-center justify-center gap-4 text-xs text-muted-foreground" aria-label="Legal" data-testid="auth-legal-nav">
          <Link to="/legal/terms" className="hover:text-primary transition-colors" data-testid="auth-footer-terms">Terms</Link>
          <span className="opacity-40">·</span>
          <Link to="/legal/privacy" className="hover:text-primary transition-colors" data-testid="auth-footer-privacy">Privacy</Link>
          <span className="opacity-40">·</span>
          <Link to="/legal/dpa" className="hover:text-primary transition-colors" data-testid="auth-footer-dpa">DPA</Link>
          <span className="opacity-40">·</span>
          <Link to="/legal/cookies" className="hover:text-primary transition-colors" data-testid="auth-footer-cookies">Cookies</Link>
        </nav>
        </div>
      </div>
    </div>
  );
}
